import { createRouter, createWebHistory } from 'vue-router';
import ChallengeRoutes from './challenge';
// import LoginRoutes from './login';
import AuthRoutes from './auth';
import RegisterRoutes from './register';
import HomeRoutes from './home';
import EducatorHubRoutes from './educatorHub';
import { ORG_CONTACT_ID_PATTERN } from './insights';
import OneColumnLayout from '@/layouts/OneColumnLayout.vue';
import ChallengeLayout from '@/layouts/ChallengeLayout.vue';
import LandingLayout from '@/layouts/LandingLayout.vue';
import EventSignupRoutes from './eventSignup';
import AccountRoutes from './account';
import LandingRoutes from './landing';
import StudentResourcesRoutes from './studentResources';
import { AuthStore } from '@/stores/auth';
import { errorToast } from '@/services/ToastService';
import { inject } from 'vue';
import HeroLayout from '@/layouts/HeroLayout.vue';

const router = createRouter({
  history: createWebHistory(),
  routes: [
    //Routes with the Empty Layout
    {
      path: '/',
      redirect: '/landing/unify-challenge',
      name: 'home',
      component: () => import('@/layouts/AppLayout.vue'),
      children: [
        // Register route
        {
          path: 'register',
          name: 'register-home',
          meta: {
            layout: OneColumnLayout,
          },
          children: [...RegisterRoutes],
        },

        // Auth routes
        {
          path: 'auth',
          redirect: '/auth/login',
          name: 'auth-home',
          meta: {
            layout: OneColumnLayout,
            bypassAuth: true,
            renderNav: false,
          },
          children: [...AuthRoutes],
        },

        //Routes with the Challenge Layout
        {
          path: 'challenge',
          name: 'challenge-home',
          meta: {
            layout: ChallengeLayout,
            renderNav: false,
            renderFooter: false,
          },
          children: [...ChallengeRoutes],
        },

        // Other routes
        {
          path: 'home',
          // redirect: { name: 'home-view' },
          name: 'participant-home',
          meta: {
            layout: HeroLayout,
          },
          component: () => import('@/views/home/ParticipantHome.vue'),
          children: [...HomeRoutes],
        },
        {
          path: 'event-signup',
          name: 'event-signup-home',
          meta: {
            layout: HeroLayout,
          },
          children: [...EventSignupRoutes],
        },
        // {
        //   path: 'nav-test',
        //   name: 'navigation-test',
        //   meta: {
        //     layout: OneColumnLayout,
        //   },
        //   component: () => import('@/views/NavigationTestView.vue'),
        // },
        {
          path: 'account',
          name: 'account-home',
          meta: {
            layout: HeroLayout,
          },
          children: [...AccountRoutes],
        },
        {
          path: 'landing',
          name: 'landing-home',
          redirect: '/landing/unify-challenge',
          meta: {
            layout: LandingLayout,
            bypassAuth: true,
            renderNav: false,
            // renderHeader: false,
          },
          children: [...LandingRoutes],
        },
        {
          path: 'educator-hub',
          name: 'educator-hub',
          redirect: '/educator-hub/home',
          meta: {
            layout: HeroLayout,
          },
          children: [...EducatorHubRoutes],
        },
        {
          path: 'attendance',
          name: 'attendance-redirect',
          redirect: '/educator-hub/attendance',
        },
        {
          path: 'insights',
          name: 'insights-redirect',
          redirect: '/educator-hub/insights',
        },
        {
          path: `insights/:orgContactId(${ORG_CONTACT_ID_PATTERN})`,
          name: 'insights-shared-redirect',
          redirect: (to) => `/educator-hub/insights/${to.params.orgContactId}`,
        },
        {
          // Top-level rather than under educator-hub so students reach it too.
          path: 'site-map',
          name: 'site-map',
          component: () => import('@/views/siteMap/SiteMap.vue' as any),
          meta: {
            layout: OneColumnLayout,
            renderNav: true,
            requireAuth: true,
            title: 'Site map',
          },
        },
        {
          path: 'student-resources',
          name: 'student-resources',
          redirect: '/student-resources/conversation-tips',
          meta: {
            layout: OneColumnLayout,
            renderNav: true,
          },
          children: [...StudentResourcesRoutes],
        },
        {
          path: 'dsr',
          name: 'dsr-view',
          component: () => import('@/views/dsr/DurableSkillsReport.vue'),
          meta: {
            layout: OneColumnLayout,
            title: 'Durable Skills Report',
          },
        },
        // Accessibility statement — Section 508 clauses 602.2 and 603.2.
        // `bypassAuth` on purpose: students and educators both need it, and support has to be
        // able to send the link to someone who is having trouble signing in, which is exactly
        // when an accessibility barrier is most likely to be the reason they cannot.
        {
          path: 'accessibility',
          name: 'accessibility',
          component: () => import('@/views/accessibility/AccessibilityFeatures.vue' as any),
          meta: {
            layout: OneColumnLayout,
            renderNav: true,
            // Anonymous visitors reach this page by a link and have access to nothing else, so
            // the nav would be a list of destinations they cannot open. Signed-in users get it.
            navRequiresAuth: true,
            // NavigationBarLogoOnly self-gates on this flag, so App.vue's route list alone
            // renders an empty <header>. Both are required for the logo to appear.
            renderLogoNav: true,
            bypassAuth: true,
            title: 'Accessibility',
          },
        },
      ],
    },
    // Routed through AppLayout like every other page — it's the only consumer of
    // `meta.layout`, so mounting the view directly left the page with no <main id="main">.
    {
      path: '/:pathMatch(.*)*',
      component: () => import('@/layouts/AppLayout.vue'),
      meta: {
        layout: OneColumnLayout,
        bypassAuth: true,
        renderNav: false,
        title: 'Page Not Found',
      },
      children: [
        {
          path: '',
          name: 'page-not-found',
          component: () => import('@/views/pageNotFound/PageNotFound.vue' as any),
        },
      ],
    },
  ],
  scrollBehavior() {
    return {
      left: 0,
      top: 0,
    };
  },
});

router.beforeEach((to, from, next) => {
  const authStore = AuthStore();
  const t: any = inject('t');

  // Always allow routes that explicitly bypass auth
  if (to.meta.bypassAuth) {
    return next();
  }

  // Dev-only: ?mockData= bypasses every API call, so let it through without a session too.
  // `import.meta.env.DEV` is statically false in a production build and this branch is
  // eliminated — verified by grepping the built output.
  if (import.meta.env.DEV && to.query.mockData) {
    return next();
  }

  // Check if user is logged in for routes that require authentication
  if (!authStore.getIsLoggedIn) {
    // If the route declared query params to preserve as cookies, save them now before
    // redirecting to login. This is for use cases like the event signup flow where we want to preserve the rescheduledChallengeUserCode across the login redirect in case it's needed after login to properly route the user.
    const paramsToPreserve = to.meta?.preserveQueryParamsAsCookies as string[] | undefined;
    if (paramsToPreserve) {
      paramsToPreserve.forEach((param) => {
        const value = to.query[param];
        if (value) {
          const expires = new Date();
          expires.setDate(expires.getDate() + 21); // 3 weeks
          document.cookie = `${param}=${encodeURIComponent(String(value))}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
        }
      });
    }

    // If user is not logged in and trying to access a protected route
    // Don't show error toast if coming from a public page to prevent noise
    if (from.meta?.bypassAuth !== true) {
      console.error(t('errors.sessionExpired'));
    }
    return next({
      name: 'login-view',
      query: { reason: 'session-expired' },
    });
  }

  // Guard for routes that explicitly require authentication
  if (to.meta.requireAuth && !authStore.getIsLoggedIn) {
    console.error(t('errors.sessionExpired'));
    return next({
      name: 'login-view',
      query: { reason: 'session-expired' },
    });
  }

  // Guard for educators-only pages
  if (to.meta.educatorsOnly && !authStore.getUser.orgContactId && !authStore.getUser.isStaff) {
    errorToast(t('errors.permissionDenied'));
    return next({ name: 'participant-home' });
  }

  return next();
});

router.afterEach((to) => {
  const pageTitle = to.meta.title as string | undefined;
  document.title = pageTitle ? `Civic Gym Hub - ${pageTitle}` : 'Civic Gym Hub';
});

export default router;
